/**
 * What each installed app carries in its rail, as data.
 *
 * Membership — not manifest scope — decides the rail inside an area app. A
 * manifest `scope` is one path prefix that must not overlap another app's, so
 * it can never say "Study also carries Notes"; this table can. Members outside
 * the manifest scope are allowed on purpose (spec §2 decision 2): client-side
 * routing never leaves an installed app, so the row works; only a hard reload
 * on that page shows the browser strip, as Study's Workspaces dashboard does
 * today.
 *
 * The first entry is the app's start tab and its home path (`appHomePaths.ts`
 * derives from it; `pwaManifests.test.ts` pins that path to the manifest).
 * Imports nothing from `shared/navigation`, so `useRail → appNav` adds no
 * cycle beside the existing `appHomePaths → destinations` edge.
 *
 * Spec: docs/plans/2026-09-16-pwa-app-nav-design.md §4.1.
 */
import type { AppSlug } from './installedApp';

/** Slug → the catalog destinations the app carries, in rail order. First entry is the start tab. */
export const APP_NAV: Record<AppSlug, readonly string[]> = {
  // Attention surfaces first — they take the phone bar's four slots; mail plumbing after.
  attention: ['inbox', 'agenda', 'starred', 'reading-room', 'paper-trail', 'bulletin-board', 'drafts', 'sent'],
  tasks: ['tasks'],
  food: ['food-diary', 'my-foods', 'meals'],
  chat: ['chat'],
  study: ['study', 'notes', 'journal', 'research'],
};

/** The destination an app opens-or-focuses on launch; an empty rail must fail at load, not launch into `/`. */
export function startTabFor(slug: AppSlug): string {
  const id = APP_NAV[slug][0];
  if (id === undefined) throw new Error(`APP_NAV.${slug} is empty`);
  return id;
}
